import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import { AppInitService } from '../../services/app-init.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private appInitService: AppInitService,
    private msalService: MsalService, // Inject MsalService
    private router: Router
  ) {}

  // Make the function async to handle promises
  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    // We need to wait for the app to be initialized before checking permissions.
    // The msalService.handleRedirectObservable() is a good proxy for this.
    // This ensures we don't act before MSAL has processed any login redirects.
    await firstValueFrom(this.msalService.handleRedirectObservable());

    const account = this.msalService.instance.getActiveAccount();

    // 1. First, check for AUTHENTICATION. Is the user even logged in?
    if (!account) {
      // The user is not authenticated. AppInitService is responsible for
      // triggering the loginRedirect. The guard's only job here is to
      // block this specific navigation attempt, which returning `false` does.
      // We do NOT redirect to 'access-denied' here.
      return false;
    }

    // 2. If we reach here, the user is authenticated. NOW we check for AUTHORIZATION.
    // By this point, AppInitService has finished and permissions are populated.
    const roleFunc = route.data['roleFunc'];
    if (!roleFunc) {
      return true; // If no specific permission is required, allow access.
    }

    const permissions = this.appInitService.permissions;
    const isAllowed = roleFunc(permissions);

    if (isAllowed) {
      return true; // User is authenticated AND authorized.
    } else {
      // User is authenticated but NOT authorized for this route.
      // This is the correct time to send them to 'access-denied'.
      return this.router.parseUrl('/access-denied');
    }
  }
}
